import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Target, TrendingUp, Apple, Camera, Plus } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface NutritionSummary {
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fats: number;
  goal_calories?: number;
  goal_protein?: number;
  goal_carbs?: number;
  goal_fats?: number;
  net_calories: number;
}

export function NutritionSummaryCard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: nutritionData, isLoading } = useQuery({
    queryKey: ['nutrition-summary', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('nutrition_daily_summary')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();
      
      return data;
    },
    enabled: !!user,
    refetchInterval: 5 * 60 * 1000,
  });

  const calculateProgress = (current: number, goal: number) => {
    return goal > 0 ? Math.min(100, Math.round((current / goal) * 100)) : 0;
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (isLoading) {
    return (
      <Card className="animate-fade-in">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="h-5 w-32 bg-muted rounded animate-pulse" />
            <Apple className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-8 w-20 bg-muted rounded animate-pulse" />
            <div className="h-2 bg-muted rounded animate-pulse" />
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-muted rounded animate-pulse" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasData = nutritionData && (nutritionData.total_calories > 0 || nutritionData.goal_calories);

  return (
    <Card className="hover-scale cursor-pointer animate-fade-in" onClick={() => navigate('/nutrition')}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span>Today's Nutrition</span>
          <Apple className="h-4 w-4 text-green-600" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasData ? (
          <div className="text-center py-4">
            <div className="text-muted-foreground text-sm mb-3">No meals logged today</div>
            <div className="flex gap-2 justify-center">
              <Button 
                size="sm" 
                variant="outline" 
                className="flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/nutrition');
                }}
              >
                <Camera className="h-3 w-3" />
                Photo
              </Button>
              <Button 
                size="sm" 
                className="flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/nutrition');
                }}
              >
                <Plus className="h-3 w-3" />
                Add Meal
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold">
                  {nutritionData.total_calories || 0}
                </span>
                <Badge variant={nutritionData.net_calories > 0 ? "default" : "secondary"}>
                  {nutritionData.net_calories > 0 ? '+' : ''}{nutritionData.net_calories} net
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                of {nutritionData.goal_calories || 0} calorie goal
              </div>
              {nutritionData.goal_calories && (
                <Progress 
                  value={calculateProgress(nutritionData.total_calories || 0, nutritionData.goal_calories)} 
                  className="h-2"
                />
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-center p-2 bg-muted/50 rounded">
                <div className="font-medium">{Math.round(nutritionData.total_protein || 0)}g</div>
                <div className="text-muted-foreground">Protein</div>
                {nutritionData.goal_protein && (
                  <div className={`w-full h-1 rounded mt-1 ${getProgressColor(calculateProgress(nutritionData.total_protein || 0, nutritionData.goal_protein))}`} 
                       style={{ width: `${calculateProgress(nutritionData.total_protein || 0, nutritionData.goal_protein)}%` }} />
                )}
              </div>
              <div className="text-center p-2 bg-muted/50 rounded">
                <div className="font-medium">{Math.round(nutritionData.total_carbs || 0)}g</div>
                <div className="text-muted-foreground">Carbs</div>
                {nutritionData.goal_carbs && (
                  <div className={`w-full h-1 rounded mt-1 ${getProgressColor(calculateProgress(nutritionData.total_carbs || 0, nutritionData.goal_carbs))}`} 
                       style={{ width: `${calculateProgress(nutritionData.total_carbs || 0, nutritionData.goal_carbs)}%` }} />
                )}
              </div>
              <div className="text-center p-2 bg-muted/50 rounded">
                <div className="font-medium">{Math.round(nutritionData.total_fats || 0)}g</div>
                <div className="text-muted-foreground">Fats</div>
                {nutritionData.goal_fats && (
                  <div className={`w-full h-1 rounded mt-1 ${getProgressColor(calculateProgress(nutritionData.total_fats || 0, nutritionData.goal_fats))}`} 
                       style={{ width: `${calculateProgress(nutritionData.total_fats || 0, nutritionData.goal_fats)}%` }} />
                )}
              </div>
            </div>
          </>
        )}
        
        <div className="pt-2 border-t">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-xs"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/nutrition');
            }}
          >
            View Nutrition Dashboard →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}